// Roadmap Builder types — mirrors TED Toolbox data model

import { GeneratedRoadmap } from "./AIRoadmapResponse"

export type Phase = 'setup' | `month-${number}`

export type EngagementDuration = 1 | 3 | 6 | 12

export interface EngagementPreset {
  duration: EngagementDuration
  label: string
  description: string
  ratePerPoint: number
  badge?: string
}

export const ENGAGEMENT_PRESETS: EngagementPreset[] = [
  { duration: 12, label: 'Annual Partnership', description: 'Full-year comprehensive engagement', ratePerPoint: 200, badge: 'Recommended' },
  { duration: 6, label: '6-Month Program', description: 'Half-year strategic program', ratePerPoint: 220 },
  { duration: 3, label: '90-Day Sprint', description: 'Quarterly engagement with setup + execution', ratePerPoint: 235, badge: 'COE Sprint' },
  { duration: 1, label: 'Single Month', description: 'One-time project engagement', ratePerPoint: 240 },
]
export interface RoadmapState {
  view: RoadmapView
  catalog: CatalogItem[]
  isLoading: boolean
  engagementConfig: EngagementConfig
  enabledServiceLines: Set<string>
  lineItems: LineItem[]
  activePhase: Phase
  clientName: string
  clientIndustry: string
  clientSize: 'smb' | 'mid_market' | 'enterprise' | ''
  clientOrgId: string
  businessModel: 'lead_gen' | 'ecommerce' | 'both' | ''
  objectives: Set<string>
  clientWebsite: string
  salesNotes: string
  aiInstructions: string
  recommendedSkus: Map<string, string> // sku → reason
  isRecommending: boolean
  recommendationSummary: string
  generatedRoadmap: GeneratedRoadmap | null
  isGeneratingRoadmap: boolean
  roadmapAccepted: boolean
}

export interface EngagementConfig {
  duration: EngagementDuration
  includeSetup: boolean
  monthlyBudget: number // points
  setupBudget: number // points
  discountPercent: number
  budgetMode: 'points' | 'dollars'
  monthlyDollarBudget: number
  setupDollarBudget: number
  startMonth: string // YYYY-MM
}

export interface CatalogItem {
  products_uuid: string
  title: string
  category_name: string
  subcategory: string
  sku: string
  service_line: string
  points: number
  price: number
  margin: number
  estimated_hours: number
  delivery_role: string
  ai_enhanced: boolean
  description: string
}

export interface LineItem extends CatalogItem {
  lineItemId: string
  phase: Phase
  quantity: number
  category: 'strategic' | 'executable'
}

export interface ServiceLineSummary {
  code: string
  label: string
  itemCount: number
  minPoints: number
  maxPoints: number
  enabled: boolean
}

export interface PhaseSummary {
  phase: Phase
  label: string
  budget: number
  usedPoints: number
  utilization: number
  itemCount: number
  strategicCount: number
  executableCount: number
  isOverBudget: boolean
}

export type RoadmapView = 'setup' | 'builder' | 'review'

export const SERVICE_LINE_LABELS: Record<string, string> = {
  seo: 'SEO',
  ana: 'Analytics',
  emm: 'Email Marketing',
  ux: 'UX / Design',
  web: 'Web Dev',
  ppc: 'Paid Media',
  content: 'Content',
  social: 'Social',
  pr: 'PR / Outreach',
}

/** Merge map: source service_line code → canonical code */
const SERVICE_LINE_MERGE: Record<string, string> = {
  creative: 'ux',
  int: 'ana',
}

/** Normalize a raw service_line code, applying merges */
export function normalizeServiceLine(raw: string): string {
  const code = (raw || 'other').toLowerCase()
  return SERVICE_LINE_MERGE[code] || code
}

export const CS_RESERVE_PCT = 0.10

// Strategic classification keywords
export const STRATEGIC_KEYWORDS = [
  'audit', 'diagnostic', 'plan', 'strategy', 'research',
  'analysis', 'assessment', 'review', 'discovery', 'roadmap',
]

export function classifyItem(item: { title: string; ta_category?: string; ta_type?: string }): 'strategic' | 'executable' {
  const combined = `${item.title} ${item.ta_category || ''} ${item.ta_type || ''}`.toLowerCase()
  return STRATEGIC_KEYWORDS.some(kw => combined.includes(kw)) ? 'strategic' : 'executable'
}

export function getPhaseLabel(phase: Phase): string {
  if (phase === 'setup') return 'Setup'
  return `Month ${phase.replace('month-', '')}`
}

export function getPhaseShortLabel(phase: Phase): string {
  if (phase === 'setup') return 'Setup'
  return `Mo ${phase.replace('month-', '')}`
}
